const NODE_WIDTH = 50
const NODE_HEIGHT = 50
const NODE_SEP = 3

export type Direction = 'horizontal' | 'vertical'

// export function createTreeRenderer

export class TreeNode<T> {
  x = 0
  y = 0
  final = 0
  modifier = 0
  prevSibling: TreeNode<T> | null = null
  children: TreeNode<T>[]
  value: T
  constructor(value: T, children: TreeNode<T>[] = []) {
    this.value = value
    this.children = children
  }
}

function visit<T>(node: TreeNode<T>, fn: (node: TreeNode<T>) => void) {
  fn(node)
  for (let i = 0; i < node.children.length; i++) {
    visit(node.children[i], fn)
  }
}

function layoutTree<T>(dataRoot: TreeNode<T>, direction: Direction) {
  const nodeRoot = prepareData(dataRoot, direction, 0, null)
  firstPass(nodeRoot, direction)
  secondPass(nodeRoot, 0, direction)
  fixNodeConflicts(nodeRoot)
  shiftTreeIntoFrame(nodeRoot)
  finalize(nodeRoot, direction)
  return nodeRoot
}

function finalize<T>(root: TreeNode<T>, direction: Direction) {
  if (direction === 'horizontal') {
    visit(root, node => {
      node.y = node.final
    })
  } else {
    visit(root, node => {
      node.x = node.final
    })
  }
}

/**
 * Build an intermediate form of the original data tree.  The nodes of
 * this new tree will be instances of the TreeNode<T> class.
 */
function prepareData<T>(
  node: TreeNode<T>,
  direction: Direction,
  level: number,
  prevSibling: TreeNode<T> | null
) {
  const treeNode = new TreeNode<T>(node.value)
  if (direction === 'horizontal') {
    treeNode.x = level
  } else {
    treeNode.y = level
  }
  treeNode.prevSibling = prevSibling

  for (let i = 0; i < node.children.length; i++) {
    treeNode.children.push(
      prepareData(
        node.children[i],
        direction,
        level + 1,
        i >= 1 ? treeNode.children[i - 1] : null
      )
    )
  }
  return treeNode
}

/**
 * Assign initial position values to every node based on how many
 * prior siblings the current node has.
 */
function firstPass<T>(node: TreeNode<T>, direction: Direction) {
  for (let i = 0; i < node.children.length; i++) {
    firstPass(node.children[i], direction)
  }
  if (direction === 'horizontal') {
    if (node.prevSibling) {
      node.y = node.prevSibling.y + NODE_SEP
    } else {
      node.y = 0
    }

    if (node.children.length == 1) {
      node.modifier = node.y
    } else if (node.children.length >= 2) {
      let minY = Infinity
      let maxY = -minY
      for (let i = 0; i < node.children.length; i++) {
        minY = Math.min(minY, node.children[i].y)
        maxY = Math.max(maxY, node.children[i].y)
      }
      node.modifier = node.y - (maxY - minY) / 2
    }
  } else {
    if (node.prevSibling) {
      node.x = node.prevSibling.x + NODE_SEP
    } else {
      node.x = 0
    }

    if (node.children.length == 1) {
      node.modifier = node.x
    } else if (node.children.length >= 2) {
      let minX = Infinity
      let maxX = -minX
      for (let i = 0; i < node.children.length; i++) {
        minX = Math.min(minX, node.children[i].x)
        maxX = Math.max(maxX, node.children[i].x)
      }
      node.modifier = node.x - (maxX - minX) / 2
    }
  }
}
/**
 * Adjust the position of children such that they end up centered
 * under their parent.
 */
function secondPass<T>(
  node: TreeNode<T>,
  modSum: number,
  direction: Direction
) {
  if (direction === 'horizontal') {
    node.final = node.y + modSum
  } else {
    node.final = node.x + modSum
  }
  for (let i = 0; i < node.children.length; i++) {
    secondPass(node.children[i], node.modifier + modSum, direction)
  }
}
/**
 * Work from the end of the tree back toward the beginning, fixing any
 * subtree overlap as we recurse through the tree.
 */
function fixNodeConflicts<T>(node: TreeNode<T>) {
  for (let i = 0; i < node.children.length; i++) {
    fixNodeConflicts(node.children[i])
  }

  for (let i = 0; i < node.children.length - 1; i++) {
    // Get the bottom-most contour position of the current node
    let botContour = -Infinity
    visit(node.children[i], node => {
      botContour = Math.max(botContour, node.final)
    })

    // Get the topmost contour position of the node underneath the current one
    let topContour = Infinity
    visit(node.children[i + 1], node => {
      topContour = Math.min(topContour, node.final)
    })

    if (botContour >= topContour) {
      visit(node.children[i + 1], node => {
        node.final += botContour - topContour + NODE_SEP
      })
    }
  }
}
/**
 * Half of the tree will naturally fall outside the container once the
 * modifier values are applied.  Here we find the minimum Y value and
 * bump the entire tree down by that value to ensure that every node
 * is in frame.
 */
function shiftTreeIntoFrame<T>(nodeRoot: TreeNode<T>) {
  let min = Infinity
  visit(nodeRoot, node => {
    min = Math.min(min, node.final)
  })

  visit(nodeRoot, node => {
    node.final += Math.abs(min)
  })
}
export type Vec2D = {
  x: number
  y: number
}
export function draw<T>(
  tree: TreeNode<T>,
  {
    width,
    height,
    direction
  }: {width: number; height: number; direction: Direction},
  cb: (
    node: TreeNode<T>,
    opts: {
      layoutSize: Vec2D
      treeSize: Vec2D
      levelSize: Vec2D
      nodeSize: Vec2D
      direction: Direction
    }
  ) => void
) {
  tree = layoutTree(tree, direction)
  const [treeWidth, treeHeight] = getDimensions(tree)

  const levelWidth = width / (treeWidth + 1)
  const levelHeight = height / (treeHeight + 1)

  iterateNodes(tree, node => {
    cb(node, {
      treeSize: {x: treeWidth, y: treeHeight},
      levelSize: {x: levelWidth, y: levelHeight},
      nodeSize: {x: NODE_WIDTH, y: NODE_HEIGHT},
      layoutSize: {x: width, y: height},
      direction
    })
  })
}
function iterateNodes<T>(tree: TreeNode<T>, cb: (node: TreeNode<T>) => void) {
  let nodes = [tree]
  while (nodes.length) {
    const node = nodes.shift()
    nodes = nodes.concat(node.children)
    cb(node)
  }
}
/**
 * Return the width and height of the tree in layout units.
 */
function getDimensions<T>(node: TreeNode<T>) {
  let minWidth = Infinity
  let maxWidth = -minWidth

  let minHeight = Infinity
  let maxHeight = -minHeight

  visit(node, node => {
    minWidth = Math.min(minWidth, node.x)
    maxWidth = Math.max(maxWidth, node.x)

    minHeight = Math.min(minHeight, node.y)
    maxHeight = Math.max(maxHeight, node.y)
  })

  return [maxWidth - minWidth, maxHeight - minHeight] as const
}
